import { useState, useEffect, useRef } from 'react';
import { SketchPicker } from 'react-color';
import fetchWithToken, { getUserData } from '../../services';

const QrCodeCustomizationTab = ({ linkId }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // QR code customization state
  const [backgroundColor, setBackgroundColor] = useState('#68C7CC');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [showProfile, setShowProfile] = useState(true);
  const [customText, setCustomText] = useState('');
  const [shape, setShape] = useState('phone');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [qrCode, setQrCode] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);

  // Predefined color options
  const colorPresets = [
    { name: 'Teal', value: '#68C7CC' },
    { name: 'Blue', value: '#007BFF' },
    { name: 'Green', value: '#28A745' },
    { name: 'Red', value: '#DC3545' },
    { name: 'Orange', value: '#FD7E14' },
    { name: 'Purple', value: '#6F42C1' },
    { name: 'Pink', value: '#E83E8C' },
    { name: 'Yellow', value: '#FFC107' },
    { name: 'Indigo', value: '#6610F2' },
    { name: 'Slate', value: '#6C757D' },
  ];

  // Generate styled QR code preview
  const generateStyledQrCode = async () => {
    if (!linkId) {
      console.error('No link available for QR code generation');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetchWithToken('/generate-styled-qr-code', {
        method: 'POST',
        body: JSON.stringify({
          link: `${baseUrl}/${linkId}?method=qrCode`,
          backgroundColor,
          textColor,
          showProfile,
          text: customText || null, // Only send if not empty
          shape
        })
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      console.log('Styled QR code generated:', data);
      setQrCode(data.qrCode);
    } catch (error) {
      console.error('Error generating styled QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download the QR code
  const downloadQrCode = () => {
    if (!qrCode) return;

    // Create temporary link element
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = 'Tipl_QR_Code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch user's QR code customization options
  useEffect(() => {
    const fetchUserCustomizations = async () => {
      try {
        setIsLoading(true);
        setIsInitializing(true);
        let fields = ['qrCodeCustomizations'];

        if (!qrCode) {
          fields.push('qrCode');
        }

        const userData = await getUserData(fields);

        if (userData.qrCode) {
          setQrCode(userData.qrCode);
        }

        if (userData.qrCodeCustomizations) {
          const customizations = userData.qrCodeCustomizations;

          if (customizations.backgroundColor) {
            setBackgroundColor(customizations.backgroundColor);
          }

          if (customizations.textColor) {
            setTextColor(customizations.textColor);
          }

          if (customizations.showProfile !== undefined) {
            setShowProfile(customizations.showProfile);
          }

          if (customizations.text) {
            setCustomText(customizations.text);
          }

          if (customizations.shape) {
            setShape(customizations.shape);
          }

          console.log('fetched customizations');
        }
      } catch (error) {
        console.error('Error fetching user customizations:', error);
      } finally {
        setIsLoading(false);
        setTimeout(() => setIsInitializing(false), 0);
      }
    };

    fetchUserCustomizations();
  }, []);

  // Effect to generate QR code when customization options change
  useEffect(() => {
    console.log('state values: ', backgroundColor, textColor, showProfile, customText, shape, linkId);
    if (linkId && !isGenerating && !isLoading && !isInitializing) {
      // Add debounce to prevent too many requests
      const timer = setTimeout(() => {
        generateStyledQrCode();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [backgroundColor, textColor, showProfile, customText, shape, linkId]);

  return (
    <div className="md:p-6">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Customization options */}
        <div className="w-full md:w-1/3">
          <div className="p-2 md:p-5 space-y-6">

            {/* Profile image toggle */}
            <div className="max-md:pt-2 max-md:pb-5">
              <div className="flex items-center justify-start md:justify-between gap-4">
                <label htmlFor="qr-show-profile-toggle" className="font-medium text-black">Show profile image:</label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    id="qr-show-profile-toggle"
                    type="checkbox"
                    checked={showProfile}
                    onChange={() => setShowProfile(!showProfile)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                </label>
              </div>
            </div>

            {/* Custom text input */}
            <div className="max-md:pb-5">
              <h4 id="qr-custom-text-label" className="block font-medium mb-2">Text:</h4>
              <div role="radiogroup" aria-labelledby="qr-custom-text-label" className="flex flex-wrap gap-2">
                {['Support', 'Tip', 'Donate', 'Contribute'].map(text => (
                  <button
                    key={text}
                    role="radio"
                    aria-checked={customText === text}
                    onClick={() => setCustomText(text)}
                    className={`px-3 py-2 text-sm ${customText === text ? 'bg-[#68C7CC] text-white' : 'bg-gray-100 hover:bg-gray-200'} rounded-md transition-colors`}
                  >
                    {text}
                  </button>
                ))}
              </div>
            </div>

            {/* Text color */}
            <div className="relative max-md:pb-5">
              <label className="block font-medium mb-2">Text color:</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {['#FFFFFF', '#000000', '#333333', '#666666'].map(color => (
                  <button
                    key={color}
                    onClick={() => setTextColor(color)}
                    className={`w-8 h-8 rounded-full ${color === '#FFFFFF' ? 'border border-gray-300' : ''} flex items-center justify-center`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {textColor === color && (
                      <span style={{ color: color === '#FFFFFF' ? '#000000' : '#FFFFFF' }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <div
                  className="w-10 h-10 rounded-md border border-gray-300 cursor-pointer"
                  style={{ backgroundColor: textColor }}
                  onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                ></div>
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#68C7CC] w-32"
                />
              </div>
              {showTextColorPicker && (
                <div className="absolute z-10 bottom-full mb-2">
                  <div className="fixed inset-0" onClick={() => setShowTextColorPicker(false)}></div>
                  <SketchPicker
                    color={textColor}
                    onChange={(color) => setTextColor(color.hex)}
                  />
                </div>
              )}
            </div>

            {/* Background color picker */}
            <div className="relative max-md:pt-4">
              <h4 id="qr-bg-color-label" className="block font-medium mb-2">Background color:</h4>
              <div role="radiogroup" aria-labelledby="qr-bg-color-label" className="flex flex-wrap gap-2 mb-2 md:max-w-48">
                {colorPresets.map((color) => (
                  <button
                    key={color.name}
                    role="radio"
                    aria-checked={backgroundColor === color.value}
                    aria-label={`Select ${color.name} as background color`}
                    onClick={() => setBackgroundColor(color.value)}
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {backgroundColor === color.value && (
                      <span className="text-white" aria-hidden="true">✓</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Open background color picker"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowColorPicker(!showColorPicker)}
                  className="w-10 h-10 rounded-md border border-gray-300 cursor-pointer"
                  style={{ backgroundColor }}
                ></div>
                <input
                  type="text"
                  aria-label="Background color hex value"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#68C7CC] w-32"
                />
              </div>
              {showColorPicker && (
                <div className="absolute z-10 bottom-full mb-2">
                  <div className="fixed inset-0" onClick={() => setShowColorPicker(false)}></div>
                  <SketchPicker
                    color={backgroundColor}
                    onChange={(color) => setBackgroundColor(color.hex)}
                  />
                </div>
              )}
            </div>

            {/* Shape selection */}
            <fieldset className="max-md:pb-5">
              <legend className="block font-medium mb-2">Shape:</legend>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="qrShape"
                    value="phone"
                    checked={shape === 'phone'}
                    onChange={() => setShape('phone')}
                    className="w-4 h-4 text-[#68C7CC] focus:ring-[#68C7CC] border-gray-300"
                  />
                  <span className="leading-4">Phone</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="qrShape"
                    value="card"
                    checked={shape === 'card'}
                    onChange={() => setShape('card')}
                    className="w-4 h-4 text-[#68C7CC] focus:ring-[#68C7CC] border-gray-300"
                  />
                  <span className="leading-4">Card</span>
                </label>
              </div>
            </fieldset>

          </div>
        </div>

        {/* QR code preview */}
        <div className="w-full md:w-2/3 flex flex-col items-center justify-center p-2">
          <div className="md:p-6 rounded-md w-full flex flex-col items-center">
            <h3 className="font-medium mb-4 text-left max-md:w-full">Preview:</h3>

            {qrCode ? (
              <div className="flex flex-col items-center max-md:w-full">
                <div className="mb-4 overflow-hidden shadow-md max-w-64">
                  <img
                    src={qrCode}
                    alt="Styled QR Code"
                    className="max-w-full h-auto"
                  />
                </div>
                {/* Download button */}
                <button
                  onClick={downloadQrCode}
                  disabled={!qrCode || isGenerating}
                  className="w-auto mt-4 px-4 py-3 bg-[#68C7CC] text-white rounded-md font-medium hover:bg-[#5bb7bc] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isGenerating ? 'Generating...' : 'Download'}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 w-full">
                {isGenerating ? (
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#68C7CC]"></div>
                ) : (
                  <p className="text-gray-500">QR code preview will appear here</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrCodeCustomizationTab;